import { useMemo } from 'react'
import { useThemeColor } from '@hooks/useThemeColor'
import { formatMoney } from '@utils/moneyFormatter'
import { IProductPage } from '@dtos/ProductPage'

// FontAwesomeIcon e RenderHtml recebem cor resolvida (não aceitam token
// Tamagui), então lemos os valores do theme ativo.
export function useResolvedColors() {
  const text = useThemeColor('text-primary')
  const textLight = useThemeColor('icon-secondary')
  const accent = useThemeColor('accent')
  const cardBrands = useThemeColor('icon-soft')

  return { text, textLight, accent, cardBrands }
}

export type IPricing = ReturnType<typeof usePricing>

// Porta as assigns de preço do product.liquid: opção corrente, âncora,
// parcelas do SparkPay quando a opção aceita cartão, período de assinatura.
// Com parcelas do SparkPay o preço exibido é o valor da parcela única (1x) e
// a linha "ou em Nx de ..." aparece mesmo com parcela única — em assinaturas,
// só quando há mais de uma.
export function usePricing(product: IProductPage) {
  return useMemo(() => {
    const option = product.currentPaymentOption
    const isSubscription = option?.kind === 'subscription'
    const acceptsCreditCard =
      option?.acceptedPaymentMethods?.includes('credit_card') ?? false
    const installments = acceptsCreditCard ? product.sparkPayInstallments : []
    const firstInstallment = installments[0] ?? null
    const lastInstallment = installments[installments.length - 1] ?? null

    const singlePayment =
      firstInstallment?.amount != null
        ? formatMoney(firstInstallment.amount)
        : null

    const showInstallment =
      lastInstallment?.value != null &&
      (!isSubscription || lastInstallment.value > 1)

    return {
      option,
      isFree: option?.kind === 'free',
      isSubscription,
      period: isSubscription ? option?.period : null,
      anchorPrice: option?.basePrice ? formatMoney(option.basePrice) : null,
      price:
        singlePayment ??
        (option?.price != null ? formatMoney(option.price) : null),
      installment: showInstallment
        ? {
            count: lastInstallment.value as number,
            amount: formatMoney(lastInstallment.amount ?? 0),
          }
        : null,
      installmentKey: isSubscription
        ? 'price.installment-line-subscription'
        : 'price.installment-line',
    }
  }, [product])
}
